import { routes } from '@dap/common/consts';
import {
  BrandProvider,
  BrandDetailsProvider,
  useGetUserQuery,
  useLanguageSelection,
  useSelectedBrandKey,
} from '@dap/common/data-access';
import { AuthorizedPageContainer, Snackbar } from '@dap/common/modules';
import { ErrorPage, LoadingPage, NotFoundPage } from '@dap/common/pages';
import { RecognizedBrands } from '@dap/common/types';
import { loadable } from '@dap/common/ui';
import { ThemeProvider, createTheme } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import AppLayout from './AppLayout';
import { config } from '@dap/app/config';
import { useAuthContext } from './contexts/Auth';
import useGoogleAnalyticsEffect from './utils/useGoogleAnalyticsEffect';
import { intranetTheme } from '@shared/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const loadingFallback = {
  fallback: <LoadingPage />,
};

const MemberadminRoutes = loadable(
  () =>
    import('@dap/memberadmin/pages').then(({ MemberadminRoutes }) => ({
      default: MemberadminRoutes,
    })),
  loadingFallback
);

const SubjectContainer = loadable(
  () =>
    import('@dap/dashboard/pages').then(({ SubjectContainer }) => ({
      default: SubjectContainer,
    })),
  loadingFallback
);

const AllEventsPage = loadable(
  () => import('@dap/dashboard/pages').then(({ AllEventsPage }) => ({ default: AllEventsPage })),
  loadingFallback
);

const AllNewsPage = loadable(
  () => import('@dap/dashboard/pages').then(({ AllNewsPage }) => ({ default: AllNewsPage })),
  loadingFallback
);

const AllServicesPage = loadable(
  () =>
    import('@dap/dashboard/pages').then(({ AllServicesPage }) => ({ default: AllServicesPage })),
  loadingFallback
);

const DashboardPage = loadable(
  () => import('@dap/dashboard/pages').then(({ DashboardPage }) => ({ default: DashboardPage })),
  loadingFallback
);

const EventPage = loadable(
  () => import('@dap/dashboard/pages').then(({ EventPage }) => ({ default: EventPage })),
  loadingFallback
);

const EventsPageOutlet = loadable(
  () =>
    import('@dap/dashboard/pages').then(({ EventsPageOutlet }) => ({ default: EventsPageOutlet })),
  loadingFallback
);

const HelpCentreOutletPage = loadable(
  () =>
    import('@dap/dashboard/pages').then(({ HelpCentreOutletPage }) => ({
      default: HelpCentreOutletPage,
    })),
  loadingFallback
);

const HelpCentrePage = loadable(
  () => import('@dap/dashboard/pages').then(({ HelpCentrePage }) => ({ default: HelpCentrePage })),
  loadingFallback
);

const ModulePage = loadable(
  () => import('@dap/dashboard/pages').then(({ ModulePage }) => ({ default: ModulePage })),
  loadingFallback
);

const NewsArticlePage = loadable(
  () =>
    import('@dap/dashboard/pages').then(({ NewsArticlePage }) => ({ default: NewsArticlePage })),
  loadingFallback
);

const NewsPageOutlet = loadable(
  () => import('@dap/dashboard/pages').then(({ NewsPageOutlet }) => ({ default: NewsPageOutlet })),
  loadingFallback
);

const ServicePage = loadable(
  () => import('@dap/dashboard/pages').then(({ ServicePage }) => ({ default: ServicePage })),
  loadingFallback
);

const ServicesPageOutlet = loadable(
  () =>
    import('@dap/dashboard/pages').then(({ ServicesPageOutlet }) => ({
      default: ServicesPageOutlet,
    })),
  loadingFallback
);

const TopicPage = loadable(
  () => import('@dap/dashboard/pages').then(({ TopicPage }) => ({ default: TopicPage })),
  loadingFallback
);

const SanityStudio = loadable(
  () =>
    import('@dap/sanity-studio/embedded').then(({ SanityStudioContainer }) => ({
      default: SanityStudioContainer,
    })),
  loadingFallback
);

const SocialFeedsContainer = loadable(
  () =>
    import('@dap/social-feed/pages').then(({ SocialFeedsContainer }) => ({
      default: SocialFeedsContainer,
    })),
  loadingFallback
);

const FormContainer = loadable(
  () =>
    import('@dap/form/containers').then(({ FormContainer }) => ({
      default: FormContainer,
    })),
  loadingFallback
);

const SettingsPage = loadable(
  () =>
    import('@dap/notifications/pages').then(({ SettingsPage }) => ({ default: SettingsPage })),
  loadingFallback
);

const brandStyles = config.styles;

function App() {
  const env = process.env['NX_PUBLIC_REACT_APP_ENV'] || 'local';
  const { logout } = useAuthContext();
  const selectedBrandKey = useSelectedBrandKey();
  const { data: userData } = useGetUserQuery();
  const { initUserLanguage } = useLanguageSelection();
  useGoogleAnalyticsEffect();

  const currentBrandStyle =
    brandStyles[selectedBrandKey as RecognizedBrands] || brandStyles.default;

  useEffect(() => {
    if (userData) {
      initUserLanguage(userData);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.metadata.preferredLanguage]);

  const brandIcon = currentBrandStyle.favicon;

  // Determine if we're on the production dataset in the production project
  // const isSanityProductionDataset =
  //   config.sanity.dataset === 'digital-arbeidsplass' && config.sanity.projectId === 'q2buvpwa';

  // Create memoized theme for the selected brand, if it has brand colors
  const brandTheme = useMemo(() => {
    const hasBrandcolors = !!currentBrandStyle?.colors?.brand;
    return hasBrandcolors
      ? createTheme(intranetTheme, {
          palette: {
            brand: currentBrandStyle?.colors?.brand,
          },
        })
      : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandKey]);

  return (
    <ErrorBoundary
      fallback={<ErrorPage />}
      beforeCapture={(scope) => {
        scope.setTag('component', 'app');
      }}
    >
      <Helmet>
        <link rel="icon" href={brandIcon} />
        <link rel="mask-icon" href={brandIcon} color="#000000" />
        {config.gaMeasurementId && (
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${config.gaMeasurementId}`}
          />
        )}
        {config.gaMeasurementId && (
          <script type="text/javascript">
            {`window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${config.gaMeasurementId}');`}
          </script>
        )}
        {config.clarity?.id && (
          <script type="text/javascript">
            {`(function (c, l, a, r, i, t, y) {
                c[a] =
                  c[a] ||
                  function () {
                    (c[a].q = c[a].q || []).push(arguments);
                  };
                t = l.createElement(r);
                t.async = 1;
                t.src = 'https://www.clarity.ms/tag/' + i;
                y = l.getElementsByTagName(r)[0];
                y.parentNode.insertBefore(t, y);
              })(window, document, 'clarity', 'script', '${config.clarity.id}');`}
          </script>
        )}
      </Helmet>
      <ThemeProvider theme={brandTheme || intranetTheme}>
        <BrandProvider>
          <BrandDetailsProvider>
            <Snackbar />
            <Routes>
              <Route
                path={routes.root}
                element={
                  <AuthorizedPageContainer logoutCallback={logout}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <AppLayout>{selectedBrandKey && <Outlet />}</AppLayout>
                    </LocalizationProvider>
                  </AuthorizedPageContainer>
                }
              >
                <Route path={routes.module}>
                  <Route index element={<Navigate to={routes.root} replace />} />
                  <Route
                    path={`${routes.mosaic}/${routes.mosaicSlug}/*`}
                    element={<ModulePage />}
                  />
                  <Route path={`${routes.order}/${routes.orderSlug}/*`} element={<ModulePage />} />
                  <Route path={`${routes.moduleSlug}/*`} element={<ModulePage />} />
                </Route>
                <Route path={`${routes.memberadmin}/*`} element={<MemberadminRoutes />} />
                <Route path={routes.home} element={<DashboardPage config={config} />} />
                <Route path={routes.news} element={<NewsPageOutlet />}>
                  <Route path="" element={<AllNewsPage />} />
                  <Route path={routes.slug} element={<NewsArticlePage />} />
                </Route>
                <Route path={routes.services} element={<ServicesPageOutlet />}>
                  <Route path="" element={<AllServicesPage />} />
                  <Route path={routes.slug} element={<ServicePage />} />
                </Route>
                <Route path={routes.events} element={<EventsPageOutlet />}>
                  <Route path="" element={<AllEventsPage />} />
                  <Route path={routes.slug} element={<EventPage />} />
                </Route>
                <Route path={routes.helpCentre} element={<HelpCentreOutletPage />}>
                  <Route path="" element={<HelpCentrePage />} />
                </Route>
                <Route path={routes.topic}>
                  <Route path={routes.slug} element={<TopicPage config={config} />} />
                </Route>
                <Route path={`${routes.subject}/:subject/*`} element={<SubjectContainer />} />
                <Route path={`${routes.socialFeed}/*`} element={<SocialFeedsContainer />} />
                <Route path={`${routes.form}/*`} element={<FormContainer />} />
                <Route path={routes.settings} element={<SettingsPage />} />
                <Route path={`${routes.sanity}/:studio`} element={<SanityStudio />} />
                <Route path={routes.notFound} element={<NotFoundPage />} />
                <Route path="*" element={<Navigate to={routes.root} replace />} />
              </Route>
            </Routes>
          </BrandDetailsProvider>
        </BrandProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
